<template>
  <teleport to="body">
    <transition-fade :duration="200">
      <div
        v-if="active"
        ref="panelRef"
        aria-modal="true"
        :aria-labelledby="title ? `overlay-panel-${id}-title` : undefined"
        tabindex="-1"
        role="dialog"
        :aria-hidden="`${!active}`"
        class="fixed inset-0 overflow-hidden"
        :class="[$attrs.class, !active && 'hidden']"
        v-bind="$attrs"
      >
        <div
          class="fixed inset-0 bg-black/10"
          tabindex="0"
          role="button"
          data-testid="drawer-backdrop"
          @keyup.enter.exact="closePanel"
          @keyup.space.exact="closePanel"
          @click="closePanel"
        ></div>
        <div
          class="shadow-large no-scrollbar absolute bottom-0 right-0 top-0 z-10 flex w-full flex-col overflow-y-auto bg-white pb-8"
          :class="narrow ? 'px-5' : 'px-8'"
          :style="{ maxWidth }"
          role="document"
          data-testid="drawer-wrapper"
        >
          <ui-spinner v-if="loading" data-testid="drawer-loading-spinner" />
          <template v-else>
            <slot v-if="$slots.header" name="header" />
            <header v-else class="sticky top-0 z-10 flex items-center gap-2 bg-white pb-10 pt-8">
              <div v-if="title || subtitle" class="grow text-lg font-semibold">
                <h2 v-if="title" :id="`overlay-panel-${id}-title`" data-testid="drawer-title" class="text-gray-80">
                  {{ title }}
                </h2>
                <p v-if="subtitle" data-testid="drawer-subtitle" class="text-gray-60">{{ subtitle }}</p>
              </div>
              <div class="flex items-center gap-2">
                <slot name="actions">
                  <ui-button secondary small data-testid="drawer-default-close-button" @click="closePanel">
                    <x-icon class="icon-m" />
                  </ui-button>
                </slot>
              </div>
            </header>
            <div class="grow text-sm">
              <slot />
            </div>
            <footer v-if="$slots['bottom-actions']" class="flex justify-end gap-4 pt-10">
              <slot name="bottom-actions" />
            </footer>
          </template>
        </div>
      </div>
    </transition-fade>
  </teleport>
</template>

<script setup lang="ts">
import { watch, ref, nextTick } from 'vue'
import { v4 as uuid } from 'uuid'
import { TransitionFade } from '@morev/vue-transitions'
import { UiSpinner } from '@klausapp/ui-kit'
import { XIcon } from '../icons'
import UiButton from './UiButton.vue'

const props = withDefaults(
  defineProps<{
    active?: boolean
    title?: string
    subtitle?: string
    loading?: boolean
    maxWidth?: string
    narrow?: boolean
  }>(),
  {
    title: '',
    subtitle: '',
    maxWidth: '824px',
  },
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

defineOptions({
  inheritAttrs: false,
})

const id = uuid()
const panelRef = ref<HTMLElement | null>(null)

watch(
  () => props.active,
  async (isActive) => {
    const app = document.getElementById('app')
    if (!app) return

    if (isActive) {
      app.inert = true
    } else {
      app.removeAttribute('inert')
    }

    app.setAttribute('tabindex', isActive ? '-1' : '0')

    // TODO: implement focus trap
    if (isActive) {
      await nextTick()
      panelRef.value?.focus()
    }
  },
)

const closePanel = () => {
  emit('close')
}
</script>
